// The running timer, shared between the shell and every screen (PRD 64 §4).
//
// WHY A MODULE-LEVEL STORE RATHER THAN A CONTEXT: §4 puts the timer "in the
// app shell so that starting one does not mean navigating away from the work".
// The shell is the PARENT route of every firm screen, so a work item page
// starting a timer is strictly below the thing that has to display it, and no
// context a route owns can reach upwards. This is the same answer
// `businessExperienceStore` already gives for the sidebar.
//
// THE SERVER IS THE TRUTH. This holds what the server last said, so the shell
// can paint immediately and every screen agrees — but `timer_started_at` is a
// column, and a tab that has been asleep re-reads rather than counting on what
// it remembers.

import { TimeEntrySummary } from '../types/practiceTimeApi';

type Listener = (entry: TimeEntrySummary | null) => void;

let running: TimeEntrySummary | null = null;
let listeners = new Set<Listener>();
/** Null until the first read settles — "unknown" is not the same as "none". */
let loaded = false;

export function getRunningTimer(): TimeEntrySummary | null {
  return running;
}

export function hasLoadedRunningTimer(): boolean {
  return loaded;
}

export function subscribeRunningTimer(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function setRunningTimer(entry: TimeEntrySummary | null): void {
  running = entry;
  loaded = true;
  listeners.forEach(listener => listener(running));
}

/** Tests only — module state outlives a render, which is the point of it. */
export function resetRunningTimer(): void {
  running = null;
  loaded = false;
  listeners = new Set();
}

/**
 * Seconds elapsed, from the SERVER's start time. Counted here rather than
 * accumulated in an interval: a tab that slept for an hour would otherwise
 * report the seconds it was awake for.
 */
export function elapsedSeconds(entry: TimeEntrySummary, now: number = Date.now()): number {
  if (!entry.timer_started_at) return 0;
  const started = new Date(entry.timer_started_at).getTime();
  return Math.max(0, Math.floor((now - started) / 1000));
}

export function formatElapsed(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  const pad = (value: number) => String(value).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(secs)}` : `${minutes}:${pad(secs)}`;
}
